import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';

import Button from '@/components/common/ui/buttons/Button';
import Hamburger from '@/components/common/ui/buttons/Hamburger';
import Social from '@/components/common/ui/social/Social';
import Quote from '@/components/common/ygen/sections/quote/Quote';
import useContainerOffset from '@/components/hooks/useContainerOffset';
import { StyledMenu } from '@/components/layout/menu/menu.style';
import { useShowSetting } from '@/core/context/ShowSettingProvider';
import gsap from 'gsap';
import DrawSVGPlugin from 'gsap/dist/DrawSVGPlugin';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Accordion from 'react-bootstrap/Accordion';
import Modal from 'react-bootstrap/Modal';
import ReactHtmlParser from 'react-html-parser';
import reactHtmlParser from 'react-html-parser';

if (typeof window !== 'undefined') {
	gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin);
}
const MenuComponent = () => {
	const getData = useShowSetting();
	const offset = useContainerOffset('.container');
	const getMenuData = getData?.getMenu?.top_menu;
	const getMenuDataHamburger = getData?.getMenu?.hamburger_menu;
	const getSettingsData = getData?.getSettings;
	const [isOpen, setIsOpen] = useState(false);
	const menuRef = useRef(null);
	const topSectionRef = useRef(null);
	const bottomSectionRef = useRef(null);
	const isMobile =
		typeof window !== 'undefined' ? window.innerWidth <= 767 : false;
	const [activeSubmenuIndex, setActiveSubMenuIndex] = useState('');
	const [activeSubmenuIndexHover, setActiveSubMenuIndexHover] = useState('');
	const pathName = usePathname();
	const pathRef = useRef(null);
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [subMenuAccordion, setSubMenuAccordion] = useState(null);
	const pathname = usePathname();
	const isFieldMountCategory = pathname?.startsWith('/industries/');
	const isFieldMountSolutions = pathname?.startsWith('/solutions/');
	const isFieldMountProducts =
		pathname?.startsWith('/product/') && !pathname.includes('ready-stock');
	const menuTimeline = useRef(null);

	// Memoized handlers
	const handleClick = useCallback(index => {
		setActiveSubMenuIndex(index);
	}, []);

	const handleClickBack = useCallback(() => {
		setActiveSubMenuIndex('');
	}, []);

	const handleToggle = useCallback(key => {
		setSubMenuAccordion(prev => (prev === key ? null : key));
	}, []);

	const handleMouseOver = useCallback(index => {
		setActiveSubMenuIndexHover(index);
	}, []);

	const toggleMenu = useCallback(() => {
		setIsOpen(prev => !prev);
		if (pathRef.current) {
			gsap.fromTo(
				pathRef.current,
				{ drawSVG: '0%' },
				{ drawSVG: '100%', duration: 2, ease: 'power2.inOut' },
			);
		}
	}, []);

	const CloseMenu = useCallback(() => {
		setIsOpen(false);
		setSubMenuAccordion(null);
	}, []);

	// Menu animation setup
	useEffect(() => {
		if (!menuRef.current || !topSectionRef.current) return;

		if (!menuTimeline.current) {
			menuTimeline.current = gsap
				.timeline({ paused: true })
				.to(
					'.overlay',
					{
						display: 'block',
						duration: 0.3,
						opacity: 1,
						ease: 'power1.out',
					},
					0,
				)
				.to(
					menuRef.current,
					{
						visibility: 'visible',
						opacity: 1,
						duration: 0.4,
						ease: 'power2.inOut',
					},
					'-=0.1',
				)
				.to(
					topSectionRef.current.querySelectorAll('.item-wrapper'),
					{
						opacity: 1,
						y: 0,
						duration: 0.3,
						stagger: 0.04,
						ease: 'power2.out',
					},
					'-=0.3',
				);
		}

		if (isOpen) {
			menuTimeline.current.play();
			document.body.classList.add('hidden');
		} else if (menuTimeline.current) {
			menuTimeline.current.reverse();
			document.body.classList.remove('hidden');
		}
	}, [isOpen]);

	// Click outside to close menu (simplified to work with overlay)
	useEffect(() => {
		const handleClickOutside = event => {
			if (!isOpen) return;

			// Check if click is inside the menu or on the hamburger button
			const menuElement = menuRef.current;
			const hamburgerWrapper = event.target.closest('.wrapper');
			const buttonWrapper = event.target.closest('.button_wrapper');
			const overlayElement = event.target.closest('.overlay');
			const menuHamburger = event.target.closest('.menu-hamburger');

			// Close if clicking on overlay
			if (overlayElement) {
				setIsOpen(false);
				return;
			}

			// Don't close if clicking inside menu or on hamburger button/wrapper
			if (menuElement && menuElement.contains(event.target)) return;
			if (hamburgerWrapper || buttonWrapper || menuHamburger) return;

			// Close the menu
			setIsOpen(false);
		};

		if (isOpen) {
			document.addEventListener('click', handleClickOutside);
		}

		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	}, [isOpen]);

	// Escape key to close menu
	useEffect(() => {
		const handleEscapeKey = event => {
			if (event.key === 'Escape' && isOpen) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('keydown', handleEscapeKey);
		}

		return () => {
			document.removeEventListener('keydown', handleEscapeKey);
		};
	}, [isOpen]);

	// Cleanup effect only runs on unmount
	useEffect(() => {
		return () => {
			document.body.classList.remove('hidden');
			if (menuTimeline.current) {
				menuTimeline.current.kill();
				menuTimeline.current = null;
			}
		};
	}, []);

	useEffect(() => {
		if (
			typeof window !== 'undefined' &&
			window.location.hash === '#get-a-quote'
		) {
			setModalIsOpen(true);
		}
	}, []);

	// Close menu and reset states when route changes
	useEffect(() => {
		// Close hamburger menu
		if (isOpen) {
			setIsOpen(false);
		}

		// Reset submenu states
		setActiveSubMenuIndex('');
		setActiveSubMenuIndexHover('');
		setSubMenuAccordion(null);

		// Remove body class if it was added
		document.body.classList.remove('hidden');
	}, [pathName]); // Listen to pathname changes

	// Add this globally or in a nav component
	const handleScrollTo = (e, scrollInstance, router) => {
		if (pathname === '/about') {
			e.preventDefault();
		}

		const href = e.currentTarget.getAttribute('href'); // e.g. /about#corporate
		const [path, hash] = href.split('#');

		if (!hash) return;

		const targetId = `#${hash}`;
		sessionStorage.setItem('scrollToHash', targetId);

		// Case 1: If already on same page (/about), force refresh
		if (window.location.pathname === path || path === '') {
			// Manually trigger scroll from here
			const el = document.querySelector(targetId);
			if (el && scrollInstance) {
				const timeoutId = setTimeout(() => {
					scrollInstance.scrollTo(el, {
						offset: -50,
						duration: 1.2,
						easing: [0.76, 0, 0.24, 1],
					});
					window.history.pushState(
						null,
						'',
						`${window.location.pathname}${targetId}`,
					);
				}, 200);
				// Note: In a real implementation, you'd want to store this timeout ID for cleanup
			}
		} else {
			// Navigate to different route
			router.push(path);
		}
	};
	const contentTransition = { duration: 0.5, ease: 'easeInOut' };

	// memorise data for menu

	return (
		<StyledMenu className={`site-menu `} offset={offset}>
			{/*top menu header */}
			<div className="top_header">
				<Container>
					<Row>
						<Col md={{ span: 6, offset: 6 }}>
							<div className="d-flex">
								{getSettingsData?.contact_email && (
									<div className="single-item">
										<Link
											href={`mailto:${getSettingsData?.contact_email}`}>
											<svg
												width="12"
												height="10"
												viewBox="0 0 12 10"
												fill="none"
												xmlns="http://www.w3.org/2000/svg">
												<path
													d="M7.00287 6.34594C6.70432 6.54497 6.35754 6.65019 6 6.65019C5.64248 6.65019 5.2957 6.54497 4.99716 6.34594L0.0798984 3.06767C0.0526172 3.04948 0.0260391 3.03052 0 3.01102V8.38283C0 8.99872 0.499805 9.48751 1.10468 9.48751H10.8953C11.5112 9.48751 12 8.9877 12 8.38283V3.011C11.9739 3.03054 11.9473 3.04955 11.9199 3.06776L7.00287 6.34594Z"
													fill="white"
												/>
												<path
													d="M0.469922 2.48263L5.38718 5.76092C5.57332 5.88502 5.78665 5.94706 5.99998 5.94706C6.21333 5.94706 6.42668 5.885 6.61282 5.76092L11.5301 2.48263C11.8243 2.28657 12 1.95845 12 1.6043C12 0.995375 11.5046 0.5 10.8957 0.5H1.1043C0.495398 0.500023 0 0.995398 0 1.60489C0 1.95845 0.175687 2.28657 0.469922 2.48263Z"
													fill="white"
												/>
											</svg>
											<span>
												{
													getData?.getSettings
														?.contact_email
												}
											</span>
										</Link>
									</div>
								)}
								{getSettingsData?.office_phone && (
									<div className="single-item">
										<Link
											href={`tel:${getSettingsData?.office_phone}`}>
											<svg
												width="12"
												height="12"
												viewBox="0 0 12 12"
												fill="none"
												xmlns="http://www.w3.org/2000/svg">
												<path
													d="M11.6608 8.80678L9.98615 7.13215C9.38806 6.53406 8.37132 6.77332 8.13209 7.55081C7.95266 8.0891 7.35458 8.38815 6.8163 8.26851C5.62013 7.96946 4.00531 6.41445 3.70627 5.15847C3.52684 4.62017 3.88569 4.02209 4.42397 3.84269C5.20148 3.60345 5.44071 2.58671 4.84263 1.98863L3.16799 0.313994C2.68953 -0.104665 1.97183 -0.104665 1.55317 0.313994L0.416809 1.45035C-0.71955 2.64652 0.536425 5.81636 3.34742 8.62735C6.15841 11.4383 9.32825 12.7542 10.5244 11.558L11.6608 10.4216C12.0795 9.94314 12.0795 9.22544 11.6608 8.80678Z"
													fill="white"
												/>
											</svg>

											<span>
												{getSettingsData?.office_phone}
											</span>
										</Link>
									</div>
								)}

								<div className="single-item">
									<Social
										facebook={getSettingsData?.facebook}
										linkedin={getSettingsData?.linkedin}
										twitter={getSettingsData?.twitter}
										youtube={getSettingsData?.youtube}
									/>
								</div>
							</div>
						</Col>
					</Row>
				</Container>
			</div>
			{/*top menu header */}

			{/*middle menu 			*/}
			<div className="middle_content">
				<Container>
					<Row className={'menu-row'}>
						<Col md={1} className={'hamburger-btn'}>
							<Link href={'/'} prefetch={true}>
								<img src="/images/static/logo.svg" alt="YGEN Automation" />
								<img
									src="/images/static/logo-black.svg"
									alt="YGEN Automation"
								/>
							</Link>
						</Col>
						<Col md={10}>
							<ul className={'middle-menu'}>
								{getMenuData?.length > 0 &&
									getMenuData.map((item, index) => {
										return (
											<li
												key={`menu-${item.item_url}`}
												onMouseEnter={() =>
													handleMouseOver(index)
												}
												onMouseLeave={() => {
													setActiveSubMenuIndexHover(
														null,
													);
												}}
												className={`${pathName === item?.item_url ? 'active' : ''} ${item?.item_url === '/industries-empowered' && isFieldMountCategory === true ? 'active' : item?.item_url === '/solution-spectrum' && isFieldMountSolutions === true ? 'active' : item?.item_url === '/products' && isFieldMountProducts === true ? 'active' : ''}`}>
												<Link
													href={item?.item_url}
													prefetch={true}>
													{reactHtmlParser(
														item?.item_title,
													)}
												</Link>
												{item?.items?.length > 0 && (
													<AnimatePresence>
														{activeSubmenuIndexHover ===
															index && (
															<motion.div
																key={`submenu-${item.item_url}`}
																initial={{
																	opacity: 0,
																	height: 0,
																	maxHeight: 0,
																	visibility:
																		'hidden',
																	zIndex: '-99',
																}}
																animate={{
																	opacity: 1,
																	height: 'auto',
																	maxHeight:
																		'calc(100vh - 120px)',
																	overflowX:
																		'hidden',
																	overflowY:
																		'scroll',
																	visibility:
																		'visible',
																	zIndex: '12',
																}}
																exit={{
																	opacity: 0,
																	height: 0,
																	maxHeight: 0,
																	overflow:
																		'hidden',
																	visibility:
																		'hidden',
																	zIndex: '-99',
																}}
																transition={
																	contentTransition
																}
																className="sub-menu-wrapper sub-menu-1">
																<motion.div
																	initial={{
																		opacity: 0,
																	}}
																	animate={{
																		opacity: 1,
																	}}
																	exit={{
																		opacity: 0,
																	}}
																	transition={{
																		duration: 0.5,
																		delay: 0.2,
																		ease: [
																			0.25,
																			0.74,
																			0.22,
																			0.99,
																		],
																	}}
																	className="top">
																	<ul>
																		{item?.items.map(
																			(
																				subItem,
																				subIndex,
																			) => {
																				const isFieldMountCategory =
																					pathname?.startsWith(
																						'/industries/',
																					);
																				const industrySlugOwn =
																					isFieldMountCategory
																						? subItem?.item_url.replace(
																								'/industries',
																								'',
																							)
																						: '';
																				return (
																					<li
																						className={`${
																							pathName ===
																							subItem?.item_url
																								? 'active'
																								: ''
																						}`}
																						onClick={() => {
																							setActiveSubMenuIndexHover(
																								null,
																							);
																						}}
																						key={
																							subItem.id ||
																							subItem.item_url ||
																							`${item.item_url}-${subIndex}`
																						}>
																						{subItem?.item_url === '#' ? (
																							<span
																								className={`no-icon menu-category-header`}
																								role="presentation"
																								aria-hidden="true">
																								{reactHtmlParser(
																									subItem?.item_title,
																								)}
																							</span>
																						) : (
																							<Link
																								className={`${subItem?.item_icon ? '' : 'no-icon'}`}
																								onClick={() => {
																									setActiveSubMenuIndexHover(
																										null,
																									);
																								}}
																								href={
																									subItem?.item_url
																								}
																								prefetch={
																									true
																								}>
																								{subItem?.item_icon && (
																									<img
																										src={
																											subItem?.item_icon
																										}
																										alt=""
																									/>
																								)}
																								<span>
																									{reactHtmlParser(
																										subItem?.item_title,
																									)}
																								</span>
																							</Link>
																						)}

																						{subItem?.items &&
																							subItem
																								?.items
																								?.length >
																								0 && (
																								<ul
																									className={
																										'd-flex flex-column sub-item-deep'
																									}>
																									{subItem?.items &&
																										subItem
																											?.items
																											?.length >
																											0 &&
																										subItem?.items?.map(
																											(
																												e,
																												index,
																											) => {
																												return (
																													<li
																														key={
																															index
																														}
																														className={`sub-item ${pathName === e?.item_url ? 'active' : ''}`}>
																														<Link
																															onClick={() => {
																																setActiveSubMenuIndexHover(
																																	null,
																																);
																															}}
																															href={
																																e?.item_url
																															}
																															prefetch={
																																true
																															}>
																															<img
																																height={
																																	20
																																}
																																src={
																																	e?.item_icon
																																		? e?.item_icon
																																		: '/images/static/default.png'
																																}
																																alt=""
																															/>
																															<span>
																																{reactHtmlParser(
																																	e?.item_title,
																																)}
																															</span>
																														</Link>
																													</li>
																												);
																											},
																										)}
																								</ul>
																							)}
																					</li>
																				);
																			},
																		)}
																	</ul>
																</motion.div>
																<motion.div
																	initial={{
																		opacity: 0,
																	}}
																	animate={{
																		opacity: 1,
																	}}
																	exit={{
																		opacity: 0,
																	}}
																	transition={{
																		duration: 1,
																		delay: 0.4,
																		ease: [
																			0.25,
																			0.74,
																			0.22,
																			0.99,
																		],
																	}}
																	className="bottom">
																	<div className="d-flex">
																		{item?.item_desc && (
																			<h6>
																				{reactHtmlParser(
																					item?.item_desc,
																				)}
																			</h6>
																		)}
																		<Button
																			text={
																				'View All ' +
																				item?.item_title
																			}
																			margin={
																				'0'
																			}
																			src={
																				item?.item_url
																			}
																			background={
																				'#152637'
																			}
																			pathColor={
																				'white'
																			}
																			color={
																				'#152637'
																			}
																		/>
																	</div>
																</motion.div>
															</motion.div>
														)}
													</AnimatePresence>
												)}
											</li>
										);
									})}
							</ul>
						</Col>
						<Col
							md={1}
							className={
								'button_wrapper d-flex align-items-center justify-content-end'
							}>
							<div onClick={toggleMenu} className="wrapper">
								<Hamburger open={isOpen} />
							</div>
						</Col>
					</Row>
				</Container>
			</div>
			<div className="overlay" onClick={CloseMenu}></div>

			{/*main menu hamburger*/}
			<div ref={menuRef} className="menu-overlay">
				<div ref={topSectionRef} className="top-section p-0 container">
					{getMenuDataHamburger &&
						getMenuDataHamburger?.length > 0 &&
						getMenuDataHamburger?.map((e, index) => {
							return (
								<div key={index} className="relative">
									<div className={'item-wrapper'}>
										{e?.items?.length > 0 ? (
											<Accordion
												activeKey={subMenuAccordion}>
												<Accordion.Item
													eventKey={index.toString()}
													key={index}>
													<Accordion.Header
														onClick={() =>
															handleToggle(
																index.toString(),
															)
														}>
														{ReactHtmlParser(
															e.item_title,
														)}
													</Accordion.Header>
													<Accordion.Body>
														{e?.items?.map(
															(
																subItem,
																subIndex,
															) => {
																return (
																	<div
																		key={
																			subIndex
																		}
																		className="item-single">
																		{subItem?.item_desc ? (
																			<Link
																				onClick={
																					CloseMenu
																				}
																				prefetch={
																					true
																				}
																				href={
																					subItem?.item_url
																				}
																				className={`nav-item`}>
																				{ReactHtmlParser(
																					subItem?.item_title,
																				)}
																			</Link>
																		) : (
																			<Link
																				onClick={
																					CloseMenu
																				}
																				prefetch={
																					true
																				}
																				href={
																					subItem?.item_url
																				}
																				className={`nav-item`}>
																				{ReactHtmlParser(
																					subItem?.item_title,
																				)}
																			</Link>
																		)}
																	</div>
																);
															},
														)}
													</Accordion.Body>
												</Accordion.Item>
											</Accordion>
										) : (
											<Link
												onClick={CloseMenu}
												prefetch={true}
												href={e?.item_url}
												className={`nav-item`}>
												{ReactHtmlParser(e.item_title)}
											</Link>
										)}
									</div>
								</div>
							);
						})}

					<Button
						text={'Request a Quote'}
						margin={'60px 0 0'}
						src="#get-a-quote"
						onClick={e => {
							e.preventDefault();
							CloseMenu();
							window.history.pushState(null, '', '#get-a-quote'); // updates URL
							setModalIsOpen(true); // opens modal
						}}
						background={'#152637'}
						pathColor={'white'}
						color={'#152637'}
					/>
				</div>

				<div className=" p-0 mobile-menu container">
					{getMenuData &&
						getMenuData?.length > 0 &&
						getMenuData?.map((e, index) => {
							return (
								<div key={index} className="relative">
									<div className={'item-wrapper'}>
										{e?.items?.length > 0 ? (
											<button
												type="button"
												onClick={() =>
													handleClick(index)
												}
												className={`nav-item ${pathName === e?.item_url ? 'active' : ''} ${e?.item_url === '/industries-empowered' && isFieldMountCategory === true ? 'active' : e?.item_url === '/solution-spectrum' && isFieldMountSolutions === true ? 'active' : e?.item_url === '/products' && isFieldMountProducts === true ? 'active' : ''}`}>
												{ReactHtmlParser(e.item_title)}
												<svg
													width="20"
													height="20"
													viewBox="0 0 20 20"
													fill="none"
													xmlns="http://www.w3.org/2000/svg">
													<circle
														cx="10"
														cy="10"
														r="9.5"
														stroke="#152637"
													/>
													<path
														d="M9.96448 13.5712L13.5 10.0357"
														stroke="#152637"
														strokeLinecap="round"
													/>
													<path
														d="M13.5 10.0356L9.96447 6.50003"
														stroke="#152637"
														strokeLinecap="round"
													/>
													<path
														d="M13.5 9.99992L6.5 9.99992"
														stroke="#152637"
														strokeLinecap="round"
													/>
												</svg>
											</button>
										) : (
											<Link
												onClick={CloseMenu}
												prefetch={true}
												href={e?.item_url}
												className={`nav-item ${pathName === e?.item_url ? 'active' : ''} ${e?.item_url === '/industries-empowered' && isFieldMountCategory === true ? 'active' : e?.item_url === '/solution-spectrum' && isFieldMountSolutions === true ? 'active' : e?.item_url === '/products' && isFieldMountProducts === true ? 'active' : ''}`}>
												{ReactHtmlParser(e.item_title)}
											</Link>
										)}
									</div>

									{e?.items?.length > 0 ? (
										<div
											key={'id-' + index}
											className={`sub-menu-sub ${activeSubmenuIndex === index ? 'active' : ''}`}>
											<div
												onClick={handleClickBack}
												className="back-down">
												<svg
													width="40"
													height="40"
													viewBox="0 0 40 40"
													fill="none"
													xmlns="http://www.w3.org/2000/svg">
													<circle
														cx="20"
														cy="20"
														r="20"
														transform="matrix(-1 0 0 1 40 0)"
														fill="#152637"
													/>
													<path
														d="M26 19C26.5523 19 27 19.4477 27 20C27 20.5523 26.5523 21 26 21V19ZM13.2929 20.7071C12.9024 20.3166 12.9024 19.6834 13.2929 19.2929L19.6569 12.9289C20.0474 12.5384 20.6805 12.5384 21.0711 12.9289C21.4616 13.3195 21.4616 13.9526 21.0711 14.3431L15.4142 20L21.0711 25.6569C21.4616 26.0474 21.4616 26.6805 21.0711 27.0711C20.6805 27.4616 20.0474 27.4616 19.6569 27.0711L13.2929 20.7071ZM26 21H14V19H26V21Z"
														fill="white"
													/>
												</svg>
												<p>
													{' '}
													{ReactHtmlParser(
														e.item_title,
													)}
												</p>
											</div>

											<div className="content-menu">
												<h6>
													{ReactHtmlParser(
														e.item_desc,
													)}
												</h6>
												<Button
													text={`View All ${ReactHtmlParser(e.item_title)}`}
													margin={'20px 0 0'}
													src={e?.item_url}
													background={'#152637'}
													pathColor={'white'}
													color={'#152637'}
												/>
											</div>

											{e?.items?.map(
												(subItem, subIndex) => {
													return (
														<div
															key={subItem?.id}
															className="relative">
															<div
																className={
																	'item-wrapper'
																}>
																<Link
																	onClick={
																		CloseMenu
																	}
																	prefetch={
																		true
																	}
																	href={
																		subItem?.item_url
																	}
																	className={`nav-item ${pathName === subItem?.item_url ? 'active' : ''}`}>
																	{subItem?.item_icon && (
																		<img
																			src={
																				subItem?.item_icon
																			}
																			alt=""
																		/>
																	)}
																	{reactHtmlParser(
																		subItem?.item_title,
																	)}
																</Link>
															</div>
														</div>
													);
												},
											)}
										</div>
									) : null}
								</div>
							);
						})}

					<Button
						text={'Request a Quote'}
						margin={'60px 0 0'}
						src="#get-a-quote"
						onClick={e => {
							e.preventDefault();
							CloseMenu();
							window.history.pushState(null, '', '#get-a-quote'); // updates URL
							setModalIsOpen(true); // opens modal
						}}
						background={'#152637'}
						pathColor={'white'}
						color={'#152637'}
					/>
				</div>

				<div className="fix-it">
					<svg
						width="414"
						height="768"
						viewBox="0 0 414 768"
						fill="none"
						xmlns="http://www.w3.org/2000/svg">
						<path
							ref={pathRef}
							opacity="0.2"
							d="M217.737 755.906C141.45 808.664 50.716 836.541 -42.0329 835.719L-40.9011 685.148C-32.1263 685.221 -23.3441 684.912 -14.5544 684.22C38.1275 679.964 88.7649 661.934 132.282 631.938C175.799 601.942 210.666 561.033 233.388 513.314C277.943 419.358 272.576 304.862 209.317 213.089C181.873 173.212 145.208 140.548 102.439 117.873C59.6694 95.1975 12.0586 83.1806 -36.3489 82.843L-35.2119 -67.7326C-17.3377 -67.5888 0.512105 -66.382 18.243 -64.1186C141.264 -48.4763 257.368 17.4883 333.291 127.634C409.215 237.779 429.545 369.747 400.398 490.291C374.396 598.346 309.332 692.959 217.737 755.906Z"
							fill="url(#paint0_linear_506_5626)"
						/>
						<defs>
							<linearGradient
								id="paint0_linear_506_5626"
								x1="339.706"
								y1="436.307"
								x2="162.905"
								y2="623.353"
								gradientUnits="userSpaceOnUse">
								<stop stopColor="#152637" stopOpacity="0" />
								<stop offset="1" stopColor="#152637" />
							</linearGradient>
						</defs>
					</svg>
				</div>
			</div>
			<Modal
				className={'modal-video'}
				show={modalIsOpen}
				onclick={() => {
					setModalIsOpen(false);
					window.location.hash = ''; // fallback method
				}}>
				<Modal.Body className={'video'}>
					<Quote
						show={modalIsOpen}
						onclick={() => {
							setModalIsOpen(false);
							window.location.hash = ''; // fallback method
						}}
					/>
				</Modal.Body>
			</Modal>
		</StyledMenu>
	);
};

export default React.memo(MenuComponent);
